import { useCallback, useEffect, useMemo, useState } from "react";
import isEmpty from "lodash/isEmpty";
import { useParams } from "react-router-dom";
import {
  ClipboardList,
  FolderOpen,
  Gem,
  Handshake,
  Mail,
  Megaphone,
  MessagesSquare,
  Network,
  Server,
  SquarePen,
  StickyNote,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import ACL_RELATIONSHIPS from "acl-relationships";
import withRoleCheck from "components/hocs/withRoleCheck";
import { linkedTabSections, useLinkedTables } from "components/linkedTables";
import RevenueOpportunityTable from "components/RevenueOpportunitiesTableV3/RevenueOpportunitiesTableV3";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import { SECTION_FILL_CLASSES } from "components/sidebar/sidebarShell";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import AuditLog from "components/views/AuditLog";
import NotesView from "components/views/NotesView";
import ResponsesView from "components/views/ResponsesView";
import CONFIG from "Config";
import { noTag } from "constants/Tag";
import setPageTitle from "helpers/title";
import { useAppTabs } from "hooks/useAppTabs";
import useAuthorized from "hooks/useAuthorized";
import useShowDisabledTags from "hooks/useShowDisabledTags";
import { SendEmailModal as _SendEmailModal } from "routes/campaign/components/Modals";
import CasesTable from "routes/case/CasesTableV3";
import EntityContactsTable from "routes/contact/components/contacts_table/ContactsTableV3";
import CampaignTable from "routes/entity/components/campaign_table/CampaignTableV3";
import ContractTable from "routes/entity/components/contracts_table/ContractsTableV3";
import DepartmentTableV3 from "routes/entity/components/departments_table/DepartmentsTableV3";
import EntityInfoPanel from "routes/entity/components/EntityInfoPanel";
import { useEntityNavigation } from "routes/entity/useEntityNavigation";
import MachinesTableV3 from "routes/machine/MachinesTableV3";
import ActivityService from "services/Activity";
import AuthService from "services/Auth";
import EntityService from "services/Entity";

const EDIT_ACLS = [ACL_RELATIONSHIPS.entity.edit, ACL_RELATIONSHIPS.user.read];

const SendEmailModal = withRoleCheck(_SendEmailModal, [
  ACL_RELATIONSHIPS.emailTemplates.read,
  ACL_RELATIONSHIPS.entityEmail.create,
]);

const CALL_ACLS = [
  ACL_RELATIONSHIPS.dialSession.read,
  ACL_RELATIONSHIPS.dialSessionPrivate.create,
  ACL_RELATIONSHIPS.callQueueNextContact.read,
  ACL_RELATIONSHIPS.callQueueAddContact.create,
  ACL_RELATIONSHIPS.disposition.read,
  ACL_RELATIONSHIPS.emailUrlForms.read,
  ACL_RELATIONSHIPS.voicemailForFilters.read,
  ACL_RELATIONSHIPS.twilioToken.read,
  ACL_RELATIONSHIPS.tag.read,
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.campaignStatuses.read,
  ACL_RELATIONSHIPS.twilioDisposition.create,
  ACL_RELATIONSHIPS.twilioAddToCall.create,
  ACL_RELATIONSHIPS.twilioRemoveFromCall.create,
  ACL_RELATIONSHIPS.twilioUpdateParticipant.create,
];

const Entity = () => {
  const { id: entityId } = useParams();
  const [auditLogId, setAuditLogId] = useState(null);
  const [model, setModel] = useState({});
  const [hasNotes, setHasNotes] = useState(false);
  const [updateAuditLogFlag, setUpdateAuditLogFlag] = useState(0);
  const showDisabledTags = useShowDisabledTags();
  const canEdit = useAuthorized(EDIT_ACLS);
  const canCall = useAuthorized(CALL_ACLS);
  const { openEdit } = useEntityNavigation();

  const tabs = useAppTabs({
    basePath: `/entities/${entityId}`,
    defaultTab: "activityLog",
  });
  const { activeTab, handleTabChange: onTabChange } = tabs;
  const { linkedTabs, isLoading: isLoadingLinkedTabs } = useLinkedTables(
    "Entity",
    entityId
  );

  const fetchNotes = useCallback(async () => {
    if (!entityId) return;
    const result = await ActivityService.hasNotes(entityId, "Entity");
    setHasNotes(result);
  }, [entityId]);

  const fetchEntity = useCallback(async () => {
    let model = await EntityService.getEntity(entityId, {
      show_disabled_tags: showDisabledTags ? "true" : "false",
    });

    if (model.tags.length === 0) {
      model.tags = [noTag];
    }
    setModel(model);
    setPageTitle(model.name);
    setUpdateAuditLogFlag(prev => prev + 1);
  }, [entityId, showDisabledTags]);

  useEffect(() => {
    if (AuthService.isLoggedIn() && entityId) {
      (async () => {
        await fetchEntity();
        await fetchNotes();
      })();
    }
  }, [entityId, fetchNotes, fetchEntity]);

  const fetchEmailedForms = useCallback(
    async filters => {
      filters = filters || {};
      return await EntityService.getEmails(entityId, filters);
    },
    [entityId]
  );

  const handleTabChange = useCallback(
    (_e, { name, auditLogId = null }) => {
      setAuditLogId(auditLogId);
      onTabChange(undefined, { name });
    },
    [onTabChange]
  );

  // The Activity Log deep-link (set by a note's "See Original") applies once.
  // Clear it when the active section changes away from the Activity Log so a
  // later return shows the newest page, not the old entry.
  const [lastSection, setLastSection] = useState(activeTab);
  if (activeTab !== lastSection) {
    setLastSection(activeTab);
    if (activeTab !== "activityLog") {
      setAuditLogId(null);
    }
  }

  const sections = useMemo(() => {
    const items = [
      {
        key: "activityLog",
        label: "Activity Log",
        icon: ClipboardList,
        content: (
          <AuditLog
            modelId={entityId}
            modelType="Entity"
            auditLogId={auditLogId}
            updateFlag={updateAuditLogFlag}
          />
        ),
      },
      {
        key: "notes",
        label: "Notes",
        icon: StickyNote,
        badge: hasNotes && activeTab !== "notes" && (
          <span
            aria-label="Has notes"
            className="tw:size-2 tw:rounded-full tw:bg-sidebar-primary"
          />
        ),
        content: (
          <NotesView
            modelType="Entity"
            modelId={entityId}
            handleTabChange={handleTabChange}
          />
        ),
      },
      {
        key: "responses",
        label: "Responses",
        icon: MessagesSquare,
        content: (
          <ResponsesView
            modelType="Entity"
            modelId={entityId}
            fetchEmailedForms={fetchEmailedForms}
            displayRecipient={true}
          />
        ),
      },
      {
        key: "contacts",
        label: "Contacts",
        icon: Users,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <EntityContactsTable
              entityId={entityId}
              enableCalling={canCall}
              tableName="entity_contacts"
            />
          </div>
        ),
      },
      {
        key: "departments",
        label: "Departments",
        icon: Network,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <DepartmentTableV3 entityId={entityId} />
          </div>
        ),
      },
      {
        key: "campaigns",
        label: "Campaigns",
        icon: Megaphone,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <CampaignTable entityId={entityId} />
          </div>
        ),
      },
      {
        key: "contracts",
        label: "Contracts",
        icon: Handshake,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <ContractTable entityId={entityId} />
          </div>
        ),
      },
      {
        key: "revenueOpportunities",
        label: "Revenue Opportunities",
        icon: Gem,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <RevenueOpportunityTable entityId={entityId} />
          </div>
        ),
      },
      {
        key: "cases",
        label: "Cases",
        icon: FolderOpen,
        hidden: !CONFIG.ENABLE_PIRACY,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <CasesTable entityId={entityId} />
          </div>
        ),
      },
      {
        key: "machines",
        label: "Machines",
        icon: Server,
        hidden: !CONFIG.ENABLE_PIRACY,
        content: (
          <div className={SECTION_FILL_CLASSES}>
            <MachinesTableV3 entityId={entityId} tableName="machines_entity" />
          </div>
        ),
      },
    ];
    items.push(...linkedTabSections(linkedTabs, entityId));
    return items;
  }, [
    entityId,
    auditLogId,
    updateAuditLogFlag,
    handleTabChange,
    hasNotes,
    activeTab,
    fetchEmailedForms,
    linkedTabs,
    canCall,
  ]);

  return (
    <SidebarDetailLayout
      sections={sections}
      tabs={tabs}
      sectionsLoading={isLoadingLinkedTabs}
      header="Entity"
      breadcrumb={
        <DetailBreadcrumb
          parentTo="/entities"
          parentLabel="All Entities"
          current={model.name}
        />
      }
      title={model.name}
      titleActions={
        <>
          <SendEmailModal
            entityId={+entityId}
            trigger={
              <Button
                variant="outline"
                size="sm"
                disabled={!model.has_emailable_contacts}
              >
                <Mail data-icon="inline-start" />
                Email
              </Button>
            }
          />
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => openEdit(entityId)}
            >
              <SquarePen data-icon="inline-start" />
              Edit
            </Button>
          )}
        </>
      }
      info={
        <EntityInfoPanel
          entity={model}
          showDisabledTags={showDisabledTags}
          onUpdate={fetchEntity}
          isLoading={isEmpty(model)}
        />
      }
      infoLabel="Entity Details"
    />
  );
};

export default Entity;
